import { useMemo, useState, useEffect } from 'react';
import { v4 as uuid } from 'uuid';
import { FileUploadsService } from '../../../../../services/fileUploadService';
import { useNotifications } from '../../../../../hooks/useNotifications';
import { useIncidentFormContext } from '../../../context/incidentFormContext';

export const useAttachmentsField = ({ defaultValue, limit, uploadFile }) => {
  const incidentFormContext = useIncidentFormContext();
  const [hasChanges, setHasChanges] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [attachmentsMap, setAttachmentsMap] = useState(new Map());
  const { showErrorNotification } = useNotifications();
  const attachmentList = useMemo(() => {
    const itemsList = [];
    attachmentsMap.forEach((value, key) => itemsList.push({ ...value, id: key }));
    return itemsList;
  }, [attachmentsMap]);

  const updateAttachment = (itemId, data) => {
    let itemSaved = true;
    setAttachmentsMap((currentItemsMap) => {
      const currentItemData = currentItemsMap.get(itemId);
      if (limit && currentItemsMap.size >= limit && currentItemData == null) {
        itemSaved = false;
        return currentItemsMap;
      }
      const newItemData = { ...currentItemData, ...data };
      const newItemsMap = new Map(currentItemsMap);
      newItemsMap.set(itemId, newItemData);
      return newItemsMap;
    });
    setHasChanges(hasChanges || itemSaved);
    return itemSaved;
  };

  const removeAttachment = (attachmentId) => {
    setAttachmentsMap((currentItemsMap) => {
      const newItemsMap = new Map(currentItemsMap);
      newItemsMap.delete(attachmentId);
      return newItemsMap;
    });
    setHasChanges(true);
  };

  const uploadAttachment = async ({ attachmentId = uuid(), file }) => {
    const newItemData = { loading: true, hasError: false, file: null, filename: file.name };
    const success = updateAttachment(attachmentId, newItemData);
    if (!success) return;
    try {
      const uploader = uploadFile ?? incidentFormContext.uploadFile
      const result = await uploader(FileUploadsService.uploadPaths.CUSTOM_FORMS, file);
      newItemData.url = result.fileKey;
    } catch (error) {
      showErrorNotification({ title: 'error', description: 'errorUploadingFile' });
      newItemData.hasError = true;
      newItemData.file = file;
    } finally {
      newItemData.loading = false;
      updateAttachment(attachmentId, newItemData);
    }
  };

  const getValue = () => {
    return attachmentList.filter((item) => !item.hasError).map((item) => ({ fileName: item.filename, url: item.url }));
  };

  useEffect(() => {
    if (!initialized && Array.isArray(defaultValue)) {
      const entries = defaultValue.map((item) => {
        const data = {
          filename: item.fileName,
          url: item.url,
          loading: false,
        };
        return [uuid(), data];
      });
      setAttachmentsMap(new Map(entries));
      setInitialized(true);
    }
  }, [defaultValue, initialized]);

  return {
    attachmentList,
    uploadAttachment,
    removeAttachment,
    getValue,
    hasChanges,
  };
};
